import React, { useState, useEffect } from 'react';
import { languagesAPI } from '../../services/api';
import './Languages.css';

const Languages = () => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLanguage, setEditingLanguage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLanguage, setNewLanguage] = useState({
    code: '',
    name: '',
    flag: '',
    display_order: 0
  });

  useEffect(() => {
    loadLanguages();
  }, []);

  const loadLanguages = async () => {
    try {
      const response = await languagesAPI.getAllLanguages();
      setLanguages(response.data);
    } catch (error) {
      console.error('Failed to load languages:', error);
      alert('Failed to load languages');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLanguage = async (e) => {
    e.preventDefault();
    if (!newLanguage.code || !newLanguage.name || !newLanguage.flag) {
      alert('Code, name, and flag are required');
      return;
    }

    try {
      await languagesAPI.createLanguage(newLanguage);
      alert('Language added successfully!');
      setNewLanguage({ code: '', name: '', flag: '', display_order: 0 });
      setShowAddForm(false);
      loadLanguages();
    } catch (error) {
      console.error('Failed to add language:', error);
      alert(error.response?.data?.error || 'Failed to add language');
    }
  };

  const handleUpdateLanguage = async (e) => {
    e.preventDefault();
    try {
      await languagesAPI.updateLanguage(editingLanguage.id, editingLanguage);
      alert('Language updated successfully!');
      setEditingLanguage(null);
      loadLanguages();
    } catch (error) {
      console.error('Failed to update language:', error);
      alert('Failed to update language');
    }
  };

  const handleDeleteLanguage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this language? This will fail if content exists for this language.')) return;
    
    try {
      await languagesAPI.deleteLanguage(id);
      alert('Language deleted successfully!');
      loadLanguages();
    } catch (error) {
      console.error('Failed to delete language:', error);
      alert(error.response?.data?.error || 'Failed to delete language');
    }
  };

  if (loading) {
    return <div className="languages-loading">Loading languages...</div>;
  }

  return (
    <div className="languages-section">
      <div className="languages-header">
        <h2>Manage Languages</h2>
        <button 
          className="btn-add-language"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add New Language'}
        </button>
      </div>

      {showAddForm && (
        <div className="add-language-form">
          <h3>Add New Language</h3>
          <form onSubmit={handleAddLanguage}>
            <div className="form-row">
              <div className="form-group">
                <label>Language Code * (2 letters, e.g., 'fr', 'es')</label>
                <input
                  type="text"
                  value={newLanguage.code}
                  onChange={(e) => setNewLanguage({...newLanguage, code: e.target.value.toLowerCase()})}
                  placeholder="e.g., fr, es, ru"
                  maxLength="2"
                  required
                />
              </div>
              <div className="form-group">
                <label>Language Name *</label>
                <input
                  type="text"
                  value={newLanguage.name}
                  onChange={(e) => setNewLanguage({...newLanguage, name: e.target.value})}
                  placeholder="e.g., Français, Español"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Flag Emoji * (Copy/paste emoji e.g., 🇫🇷)</label>
                <input
                  type="text"
                  value={newLanguage.flag}
                  onChange={(e) => setNewLanguage({...newLanguage, flag: e.target.value})}
                  placeholder="e.g., 🇫🇷, 🇪🇸, 🇷🇺"
                  required
                />
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={newLanguage.display_order}
                  onChange={(e) => setNewLanguage({...newLanguage, display_order: parseInt(e.target.value)})}
                  min="0"
                />
              </div>
            </div>
            <button type="submit" className="btn-save">Add Language</button>
          </form>
        </div>
      )}

      <div className="languages-grid">
        {languages.map(language => (
          <div key={language.id} className="language-card">
            {editingLanguage?.id === language.id ? (
              <form onSubmit={handleUpdateLanguage} className="edit-language-form">
                <div className="form-group">
                  <label>Code (read-only)</label>
                  <input
                    type="text"
                    value={editingLanguage.code}
                    disabled
                    className="input-disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editingLanguage.name}
                    onChange={(e) => setEditingLanguage({...editingLanguage, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Flag Emoji</label>
                  <input
                    type="text"
                    value={editingLanguage.flag}
                    onChange={(e) => setEditingLanguage({...editingLanguage, flag: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={editingLanguage.display_order}
                    onChange={(e) => setEditingLanguage({...editingLanguage, display_order: parseInt(e.target.value)})}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editingLanguage.is_active}
                      onChange={(e) => setEditingLanguage({...editingLanguage, is_active: e.target.checked})}
                    />
                    {' '}Active
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-save">Save</button>
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => setEditingLanguage(null)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="language-header">
                  <span className="language-flag">{language.flag}</span>
                  <div className="language-info">
                    <h3>{language.name}</h3>
                    <p className="language-code">Code: {language.code.toUpperCase()}</p>
                  </div>
                  <span className={`status-badge ${language.is_active ? 'active' : 'inactive'}`}>
                    {language.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="language-meta">
                  <span className="order-badge">Order: {language.display_order}</span>
                </div>
                <div className="language-actions">
                  <button 
                    onClick={() => setEditingLanguage(language)}
                    className="btn-edit"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteLanguage(language.id)}
                    className="btn-delete"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {languages.length === 0 && (
        <div className="no-languages">
          <p>No languages configured. Add your first language to get started!</p>
        </div>
      )}
    </div>
  );
};

export default Languages;
